/**
 * Evaluator - Static position evaluation for minimax
 *
 * Evaluates game positions from the perspective of a faction.
 * Returns positive scores when the evaluated faction is winning.
 */

import { CityLevel } from '../../../types';
import { GameMap } from '../../../Map';
import {
  GameStateSnapshot,
  UnitSnapshot,
  CitySnapshot,
  EVALUATION_WEIGHTS,
  UNIT_VALUES,
  TERRAIN_DATA,
  hexDistance,
  isPositionOnCity,
  distanceToCity,
  canUnitCapture
} from '../V3Types';
import { getUnitsForFaction, getCitiesForFaction, calculateExpectedDamage } from './GameStateSnapshot';

// Terminal evaluation scores
const CHECKMATE_SCORE = 100000;
// const STALEMATE_SCORE = 0; // Reserved for future use

/**
 * Main evaluation function
 * Returns score from perspective of snapshot.currentFaction
 */
export function evaluate(snapshot: GameStateSnapshot, map: GameMap): number {
  // Check for terminal states
  const terminalScore = checkTerminalState(snapshot);
  if (terminalScore !== null) {
    return terminalScore;
  }

  // Component scores
  const material = evaluateMaterial(snapshot);
  const cityControl = evaluateCityControl(snapshot);
  const threats = evaluateThreats(snapshot);
  const position = evaluatePosition(snapshot, map);
  const tempo = evaluateTempo(snapshot);

  // Weighted sum
  return (
    EVALUATION_WEIGHTS.MATERIAL * material +
    EVALUATION_WEIGHTS.CITY_CONTROL * cityControl +
    EVALUATION_WEIGHTS.THREATS * threats +
    EVALUATION_WEIGHTS.POSITION * position +
    EVALUATION_WEIGHTS.TEMPO * tempo
  );
}

/**
 * Check for game-ending conditions
 */
function checkTerminalState(snapshot: GameStateSnapshot): number | null {
  const ourUnits = getUnitsForFaction(snapshot, snapshot.currentFaction);
  const enemyUnits = getUnitsForFaction(snapshot, snapshot.enemyFaction);
  const ourCities = getCitiesForFaction(snapshot, snapshot.currentFaction);
  const enemyCities = getCitiesForFaction(snapshot, snapshot.enemyFaction);

  // We lost all units and cities
  if (ourUnits.length === 0 && ourCities.length === 0) {
    return -CHECKMATE_SCORE;
  }

  // Enemy lost all units and cities
  if (enemyUnits.length === 0 && enemyCities.length === 0) {
    return CHECKMATE_SCORE;
  }

  // Not terminal
  return null;
}

/**
 * Evaluate material balance
 * Returns value in range [-1, 1] normalized
 */
function evaluateMaterial(snapshot: GameStateSnapshot): number {
  let ourValue = 0;
  let enemyValue = 0;

  for (const unit of snapshot.units) {
    const baseValue = UNIT_VALUES[unit.category];
    const healthFactor = unit.health / unit.maxHealth;
    const veteranBonus = unit.isVeteran ? 1.25 : 1.0;
    const value = baseValue * healthFactor * veteranBonus;

    if (unit.faction === snapshot.currentFaction) {
      ourValue += value;
    } else {
      enemyValue += value;
    }
  }

  // Normalize to avoid dominating other factors
  const total = ourValue + enemyValue;
  if (total === 0) return 0;

  return (ourValue - enemyValue) / Math.max(1000, total);
}

/**
 * Evaluate city control
 */
function evaluateCityControl(snapshot: GameStateSnapshot): number {
  let score = 0;

  for (const city of snapshot.cities) {
    const cityValue = getCityValue(city);

    if (city.faction === snapshot.currentFaction) {
      score += cityValue;

      // Bonus for defended cities
      const defenders = countDefenders(snapshot, city);
      score += defenders * 0.1;

      // Penalty if being captured
      if (city.isBeingCaptured && city.capturingFaction !== snapshot.currentFaction) {
        score -= cityValue * 0.5;
      }
    } else if (city.faction === snapshot.enemyFaction) {
      score -= cityValue;

      // Bonus if we're capturing it
      if (city.isBeingCaptured && city.capturingFaction === snapshot.currentFaction) {
        score += cityValue * 0.3;
      }
    } else {
      // Neutral city - value based on proximity to any city tile
      const ourUnits = getUnitsForFaction(snapshot, snapshot.currentFaction);
      const enemyUnits = getUnitsForFaction(snapshot, snapshot.enemyFaction);

      const ourMinDist = getMinDistanceToCity(ourUnits, city);
      const enemyMinDist = getMinDistanceToCity(enemyUnits, city);

      // We're closer = positive, enemy closer = negative
      if (ourMinDist < enemyMinDist) {
        score += cityValue * 0.2 * (1 - ourMinDist / 20);
      } else if (enemyMinDist < ourMinDist) {
        score -= cityValue * 0.2 * (1 - enemyMinDist / 20);
      }
    }
  }

  // Normalize
  const maxCityScore = snapshot.cities.length * 3; // Max city level value
  return score / Math.max(1, maxCityScore);
}

/**
 * Evaluate threats (damage we can deal vs damage we can take)
 */
function evaluateThreats(snapshot: GameStateSnapshot): number {
  const ourUnits = getUnitsForFaction(snapshot, snapshot.currentFaction);
  const enemyUnits = getUnitsForFaction(snapshot, snapshot.enemyFaction);

  let ourThreat = 0;
  let enemyThreat = 0;

  // Calculate threat we pose to enemy
  for (const ourUnit of ourUnits) {
    if (ourUnit.hasAttacked) continue;

    for (const enemyUnit of enemyUnits) {
      const distance = hexDistance(ourUnit.position, enemyUnit.position);

      // Direct threat (in range now)
      if (distance <= ourUnit.range) {
        const damage = calculateExpectedDamage(ourUnit, enemyUnit);
        ourThreat += damage;

        // Kill threat bonus
        if (damage >= enemyUnit.health) {
          ourThreat += UNIT_VALUES[enemyUnit.category] / 10;
        }
      }
      // Potential threat - only consider very close units (range + 2 max)
      // This prevents the AI from being scared of enemies 7+ tiles away
      else if (distance <= ourUnit.range + 2) {
        const damage = calculateExpectedDamage(ourUnit, enemyUnit);
        ourThreat += damage * 0.15; // Very reduced weight for potential
      }
    }
  }

  // Calculate threat enemy poses to us
  for (const enemyUnit of enemyUnits) {
    if (enemyUnit.hasAttacked) continue;

    for (const ourUnit of ourUnits) {
      const distance = hexDistance(enemyUnit.position, ourUnit.position);

      if (distance <= enemyUnit.range) {
        const damage = calculateExpectedDamage(enemyUnit, ourUnit);
        enemyThreat += damage;

        if (damage >= ourUnit.health) {
          enemyThreat += UNIT_VALUES[ourUnit.category] / 10;
        }
      }
      // Potential threat - only consider very close units (range + 2 max)
      else if (distance <= enemyUnit.range + 2) {
        const damage = calculateExpectedDamage(enemyUnit, ourUnit);
        enemyThreat += damage * 0.15; // Very reduced weight for potential
      }
    }
  }

  // Normalize
  const maxThreat = Math.max(ourThreat, enemyThreat, 100);
  return (ourThreat - enemyThreat) / maxThreat;
}

/**
 * Evaluate positional factors
 */
function evaluatePosition(snapshot: GameStateSnapshot, map: GameMap): number {
  let score = 0;

  const ourUnits = getUnitsForFaction(snapshot, snapshot.currentFaction);
  const enemyUnits = getUnitsForFaction(snapshot, snapshot.enemyFaction);
  const ourCities = getCitiesForFaction(snapshot, snapshot.currentFaction);
  const enemyCities = getCitiesForFaction(snapshot, snapshot.enemyFaction);
  const neutralCities = snapshot.cities.filter(c => c.faction === null);

  for (const unit of ourUnits) {
    // Terrain defense value - REDUCED to not override objective-seeking
    const tile = map.getTile(unit.position.x, unit.position.y);
    if (tile) {
      const terrainData = TERRAIN_DATA[tile.terrain];
      if (terrainData) {
        score += terrainData.defenseBonus * 3;  // Reduced from 10
      }
    }

    // Formation bonus - SMART clustering based on shared objectives
    // Units going to the same objective should cluster, others should spread out
    const adjacentAllies = ourUnits.filter(a =>
      a.id !== unit.id &&
      hexDistance(unit.position, a.position) <= 2
    );

    // Check if units share the same objective (from FrontManager)
    const unitObjective = snapshot.unitObjectives?.get(unit.id);
    let sameObjectiveAllies = 0;
    let differentObjectiveAllies = 0;

    for (const ally of adjacentAllies) {
      const allyObjective = snapshot.unitObjectives?.get(ally.id);
      if (unitObjective && allyObjective &&
          unitObjective.x === allyObjective.x && unitObjective.y === allyObjective.y) {
        sameObjectiveAllies++;
      } else {
        differentObjectiveAllies++;
      }
    }

    // Bonus for clustering with units going to same objective (combined arms!)
    score += sameObjectiveAllies * 3;

    // Penalty for clustering with units going to different objectives (they should split!)
    score -= differentObjectiveAllies * 2;

    // Check unit's assigned objective - we should ONLY reward proximity to ASSIGNED target
    // This prevents units from being attracted to other cities
    // (unitObjective is already defined above for clustering logic)
    // Note: All ground units can capture, but aircraft cannot
    const canCapture = canUnitCapture(unit);

    if (unitObjective) {
      // Unit has an assigned objective - ONLY reward proximity to THAT objective
      const assignedCity = [...enemyCities, ...neutralCities].find(c =>
        isPositionOnCity(unitObjective, c) ||
        (c.centerPosition.x === unitObjective.x && c.centerPosition.y === unitObjective.y)
      );

      if (assignedCity) {
        const dist = distanceToCity(unit.position, assignedCity);
        const isNeutral = assignedCity.faction === null;

        // High bonus for proximity to ASSIGNED objective only
        const proximityBonus = Math.max(0, 25 - dist) * (canCapture ? 8 : 3);
        score += proximityBonus;

        // CRITICAL: Imminent capture bonus - when capture-capable unit is very close to neutral city
        // This bonus must be HIGH ENOUGH to override any threat evaluation
        // Otherwise the AI retreats when 1 tile away from capturing!
        if (isNeutral && canCapture && dist <= 2) {
          // Massive bonus that scales inversely with distance
          // dist=0 (on city): 800, dist=1: 600, dist=2: 400
          const imminentCaptureBonus = (3 - dist) * 200;
          score += imminentCaptureBonus;
        }

        // Extra bonus for being ON the assigned city
        if (isPositionOnCity(unit.position, assignedCity)) {
          score += (isNeutral ? 400 : 250) * (canCapture ? 2 : 1);
        }
      }
    } else {
      // No assigned objective - give smaller bonuses for proximity to any city
      // (This is a fallback for units in RESERVE or without assignments)
      for (const city of enemyCities) {
        const dist = distanceToCity(unit.position, city);
        const proximityBonus = Math.max(0, 20 - dist) * (canCapture ? 3 : 1);
        score += proximityBonus;

        if (isPositionOnCity(unit.position, city)) {
          score += 100 * (canCapture ? 2 : 1);
        }
      }

      for (const city of neutralCities) {
        const dist = distanceToCity(unit.position, city);
        const proximityBonus = Math.max(0, 20 - dist) * (canCapture ? 4 : 1);
        score += proximityBonus;

        // Imminent capture bonus for unassigned units too
        if (canCapture && dist <= 2) {
          const imminentCaptureBonus = (3 - dist) * 150;
          score += imminentCaptureBonus;
        }

        if (isPositionOnCity(unit.position, city)) {
          score += 200 * (canCapture ? 2 : 1);
        }
      }
    }

    // Proximity to ENEMY UNITS - reward being in attack range
    for (const enemy of enemyUnits) {
      const dist = hexDistance(unit.position, enemy.position);
      if (dist <= unit.range) {
        score += 20;
      } else if (dist <= unit.range + unit.movement) {
        score += 8;
      }
    }

    // LONE WOLF PENALTY: Penalize units that are close to enemies but far from allies
    // Units should group up before attacking, not advance alone
    const closestEnemyDist = enemyUnits.length > 0 ?
      Math.min(...enemyUnits.map(e => hexDistance(unit.position, e.position))) :
      Infinity;

    // Only apply lone wolf penalty if near combat (within 5 tiles of enemies)
    if (closestEnemyDist <= 5) {
      // Count nearby allied units
      const nearbyAllies = ourUnits.filter(a =>
        a.id !== unit.id &&
        hexDistance(unit.position, a.position) <= 3
      ).length;

      if (nearbyAllies === 0) {
        // Completely alone near enemies - big penalty
        score -= 150;
      } else if (nearbyAllies === 1) {
        // Only 1 ally nearby - moderate penalty
        score -= 50;
      } else {
        // 2+ allies nearby - bonus for good formation
        score += 30;
      }
    }
  }

  // Penalize enemy proximity to OUR cities
  for (const city of ourCities) {
    // Count our defenders ON this city
    const ourDefendersOnCity = ourUnits.filter(u =>
      isPositionOnCity(u.position, city)
    ).length;

    for (const enemy of enemyUnits) {
      const dist = distanceToCity(enemy.position, city);  // Use closest tile
      if (dist <= 3) {
        score -= (4 - dist) * 15; // Penalize enemies near our cities (increased)
      }
      // HUGE penalty if enemy is ON our city - they're blocking our production!
      if (isPositionOnCity(enemy.position, city)) {
        score -= 400;  // Increased from 100 - production blocking is critical
      }
    }

    // GARRISON BONUS: If we have exactly 1 defender on a city, give bonus for staying
    // This unit is the only thing preventing capture!
    if (ourDefendersOnCity === 1) {
      // Find that defender and give them a bonus for being there
      const defender = ourUnits.find(u => isPositionOnCity(u.position, city));
      if (defender) {
        // This unit is critical - bonus for staying, implicit penalty if it leaves
        score += 300;
      }
    } else if (ourDefendersOnCity === 0) {
      // NO defenders on city - this is dangerous if enemies are near
      const enemiesNear = enemyUnits.filter(u => distanceToCity(u.position, city) <= 4);
      if (enemiesNear.length > 0) {
        score -= 200 * enemiesNear.length;  // Penalty for undefended city
      }
    }
  }

  // CITY BLOCKING BONUS: Our units ON enemy cities block their production
  for (const city of enemyCities) {
    const ourUnitsOnCity = ourUnits.filter(u =>
      isPositionOnCity(u.position, city)
    ).length;

    if (ourUnitsOnCity > 0) {
      // Each unit ON an enemy city blocks their production - very valuable!
      score += 350 * ourUnitsOnCity;
    }
  }

  // Enemy position analysis (negative)
  for (const unit of enemyUnits) {
    const adjacentAllies = enemyUnits.filter(a =>
      a.id !== unit.id &&
      hexDistance(unit.position, a.position) <= 2
    );

    // Penalize enemy formation
    score -= adjacentAllies.length * 3;

    // Reward if enemy is isolated
    if (adjacentAllies.length === 0) {
      score += 5;
    }
  }

  // Normalize - but keep values meaningful (don't divide too aggressively)
  const maxUnits = ourUnits.length + enemyUnits.length;
  return score / Math.max(1, maxUnits * 5);
}

/**
 * Evaluate tempo (initiative/momentum)
 */
function evaluateTempo(snapshot: GameStateSnapshot): number {
  let ourTempo = 0;
  let enemyTempo = 0;

  for (const unit of snapshot.units) {
    const actionValue = (
      (unit.movementRemaining > 0 ? unit.movementRemaining : 0) +
      (!unit.hasAttacked ? 2 : 0)
    );

    if (unit.faction === snapshot.currentFaction) {
      ourTempo += actionValue;
    } else {
      enemyTempo += actionValue;
    }
  }

  const maxTempo = snapshot.units.length * 10;
  return (ourTempo - enemyTempo) / Math.max(1, maxTempo);
}

/**
 * Get city value based on level
 */
function getCityValue(city: CitySnapshot): number {
  switch (city.level) {
    case CityLevel.OUTPOST: return 1;
    case CityLevel.FACTORY: return 2;
    case CityLevel.AIRBASE: return 3;
    default: return 1;
  }
}

/**
 * Count defenders near a city
 */
function countDefenders(snapshot: GameStateSnapshot, city: CitySnapshot): number {
  const ourUnits = getUnitsForFaction(snapshot, snapshot.currentFaction);
  return ourUnits.filter(u => hexDistance(u.position, city.centerPosition) <= 2).length;
}

/**
 * Get minimum distance from any unit to the closest tile of a city
 */
function getMinDistanceToCity(units: UnitSnapshot[], city: CitySnapshot): number {
  if (units.length === 0) return Infinity;

  let minDist = Infinity;
  for (const unit of units) {
    const dist = distanceToCity(unit.position, city);
    if (dist < minDist) {
      minDist = dist;
    }
  }
  return minDist;
}

/**
 * Quick evaluation for move ordering (lighter weight)
 */
export function quickEvaluate(snapshot: GameStateSnapshot): number {
  // Only material and city control for speed
  const material = evaluateMaterial(snapshot);
  const cityControl = evaluateCityControl(snapshot);

  return (
    EVALUATION_WEIGHTS.MATERIAL * material +
    EVALUATION_WEIGHTS.CITY_CONTROL * cityControl
  );
}

/**
 * Evaluate a specific move's immediate impact
 */
export function evaluateMove(
  _snapshot: GameStateSnapshot,
  move: import('../V3Types').GeneratedMove
): number {
  let score = move.priority; // Start with priority

  // Killing blows are very valuable
  if (move.flags.isKillingBlow) {
    score += 500;
  }

  // City captures
  if (move.flags.capturesCity) {
    score += 300;
  }

  // Threatening cities
  if (move.flags.threatensCity) {
    score += 100;
  }

  // Supporting allies
  if (move.flags.supportsAlly) {
    score += 50;
  }

  // Position improvement
  if (move.flags.improvesPosition) {
    score += 30;
  }

  return score;
}
